import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth, db } from "../firebase-config";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./EmployeeDashboard.css";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  updateDoc,
  setDoc,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { AbsencePopup } from "./EmployeeAbsenceList";
import DisclaimerModal from "./DisclaimerModal";
import { calculateAdjustedTimes, calculateAdjustedWorkTime } from "../services/timeAdjustmentService";
import { enqueue, flushQueue, getPendingCount, getQueue } from "../services/pendingActionsService";

/* ──────────────────────────────── Constantes ──────────────────────────────── */
const MAP_STYLES = [
  {
    key: "osm",
    label: "OpenStreetMap Standard",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; OpenStreetMap contributors'
  },
  {
    key: "cartodb_dark",
    label: "CartoDB Dark Matter (sombre)",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap contributors'
  },
  {
    key: "cartodb",
    label: "CartoDB Positron (clair)",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; OpenStreetMap contributors'
  },
  {
    key: "stadia_toner",
    label: "Stadia Stamen Toner (noir & blanc)",
    url: "https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png?api_key=8130683a-487e-49be-bbce-7bf5f7a43889",
    attribution: '&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://www.stamen.com/" target="_blank">Stamen Design</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  }
];

const MIN_ACCURACY = 100; // Précision GPS minimale en mètres

const initialState = {
  status: "idle", // 'idle', 'working', 'on_break', 'confirming_end', 'recap', 'ended'
  startTime: null,
  currentBreakStartTime: null,
  sessionId: null,
  activeBreakId: null,    // ID de la pause en cours (généré localement)
  breakType: null,        // 'regular' | 'lunch'
  localPauseTime: 0,      // Cumul des pauses en ms (calculé localement)
  position: null,
  breaks: [],
};

const createLocationIcon = (isActive) =>
  L.divIcon({
    className: isActive
      ? "location-marker-active"
      : "location-marker-inactive",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    html: `
      <div class="location-marker-svg">
        <div class="pulse"></div>
        <div class="dot"></div>
      </div>
    `,
  });

/* ──────────────────────────────── Helpers ──────────────────────────────── */
const formatDateFR = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('fr-FR');
};

const formatTime = (milliseconds) => {
  if (!milliseconds) return "0h 0m";
  const totalMinutes = Math.round(milliseconds / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

/* ──────────────────────────────── Composant ──────────────────────────────── */
function EmployeeDashboard() {
  const navigate = useNavigate();
  const mapRef = useRef(null);

  /* ----------------------- États ----------------------- */
  const [userEmail, setUserEmail] = useState("");
  const [userData, setUserData] = useState(null);
  const [sessionState, setSessionState] = useState(initialState);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true); // Nouveau: chargement initial
  const [isLocationActive, setIsLocationActive] = useState(false);
  const [gpsPermissionDenied, setGpsPermissionDenied] = useState(false);
  const [mapStyle, setMapStyle] = useState(MAP_STYLES[0]);
  const [isMapStyleLoaded, setIsMapStyleLoaded] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [showAbsencePopup, setShowAbsencePopup] = useState(null);

  // États pour le disclaimer
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [employeeDocId, setEmployeeDocId] = useState(null);

  // État pour les données de récapitulatif
  const [recapData, setRecapData] = useState({
    sessionTime: 0,
    totalPauseTime: 0,
    workTime: 0
  });

  // Nombre d'actions en attente de sync
  const [pendingCount, setPendingCount] = useState(() => getPendingCount());

  // Pause déjeuner — durée minimale (en ms) chargée depuis la config
  const [minLunchBreakMs, setMinLunchBreakMs] = useState(20 * 60 * 1000); // 20 min par défaut
  // Secondes restantes pendant la pause déjeuner (null = pas de compteur actif)
  const [remainingLunchSec, setRemainingLunchSec] = useState(null);

  /* ----------------------- Connexion réseau + flush au retour ----------------------- */
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      // Flush la queue dès que le réseau revient
      try {
        const result = await flushQueue();
        setPendingCount(result.remaining);
      } catch (err) {
        console.error("Erreur flush queue:", err);
      }
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Tenter un flush au démarrage si déjà en ligne
    if (navigator.onLine && getPendingCount() > 0) {
      flushQueue()
        .then(r => setPendingCount(r.remaining))
        .catch(err => console.error("Erreur flush démarrage:", err));
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  /* ----------------------- Auth ----------------------- */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return navigate("/");
      setUserEmail(u.email);
      try {
        const q = query(collection(db, "employees"), where("email", "==", u.email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const employeeDoc = snap.docs[0];
          const employeeData = employeeDoc.data();
          setEmployeeDocId(employeeDoc.id);
          setUserData(employeeData);
          
          // Vérifier si l'utilisateur a accepté le disclaimer
          if (!employeeData.hasAcceptedDisclaimer) {
            setShowDisclaimer(true);
          }
        }
      } catch (err) {
        console.error("Erreur chargement profil:", err);
        setError("Erreur chargement profil");
      }
    });
    return unsub;
  }, [navigate]);

  /* ─── Reconstruction état depuis la queue locale (cas offline + refresh) ─── */
  const reconstructStateFromQueue = (email) => {
    const queue = getQueue();
    const actions = queue.filter(a => a.employeeEmail === email);
    if (actions.length === 0) return null;

    const startWork = [...actions].reverse().find(a => a.type === 'START_WORK');
    if (!startWork) return null;

    // Si END_WORK déjà en queue → journée terminée localement
    const endWork = actions.find(a => a.type === 'END_WORK' && a.sessionId === startWork.sessionId);
    if (endWork) return null;

    // Pause en cours ?
    const startBreak = actions.find(a => a.type === 'START_BREAK' && a.sessionId === startWork.sessionId);
    const endBreak   = startBreak ? actions.find(a => a.type === 'END_BREAK' && a.breakId === startBreak.breakId) : null;
    const hasBreak   = startBreak && !endBreak;

    return {
      status:                hasBreak ? 'on_break' : 'working',
      startTime:             new Date(startWork.timestamp),
      currentBreakStartTime: hasBreak ? new Date(startBreak.timestamp) : null,
      sessionId:             startWork.sessionId,
      activeBreakId:         hasBreak ? startBreak.breakId : null,
      breakType:             hasBreak ? (startBreak.breakType || 'regular') : null,
      localPauseTime:        0,
      position:              startWork.location
        ? { lat: startWork.location.latitude, lng: startWork.location.longitude, accuracy: startWork.location.accuracy }
        : null,
      breaks: [],
    };
  };

  /* ----------------------- Récupération session active EN TEMPS RÉEL ----------------------- */
  useEffect(() => {
    if (!userEmail) {
      setIsLoadingSession(false);
      return;
    }

    setIsLoadingSession(true);

    // 🔥 LISTENER TEMPS RÉEL sur la session active
    const sessionsQuery = query(
      collection(db, "workSessions"),
      where("employeeEmail", "==", userEmail),
      where("status", "==", "active")
    );

    const unsubscribeSession = onSnapshot(sessionsQuery, async (snapshot) => {
      try {
        if (!snapshot.empty) {
          const sessionDoc = snapshot.docs[0];
          const sessionData = sessionDoc.data();
          
          // Vérifier si la session n'est pas trop ancienne (> 24h)
          const startTime = sessionData.startTime.toDate();
          const hoursSinceStart = (Date.now() - startTime.getTime()) / (1000 * 60 * 60);
          
          if (hoursSinceStart > 24) {
            console.warn("Session de plus de 24h détectée, clôture automatique");
            await finalizeSessionInFirebase(sessionDoc.id, null);
            setSessionState(initialState);
            setIsLoadingSession(false);
            return;
          }
          
          // 🔥 LISTENER TEMPS RÉEL sur les pauses de cette session
          const breaksQuery = query(
            collection(db, "breaks"),
            where("sessionId", "==", sessionDoc.id),
            orderBy("startTime", "asc")
          );
          
          const unsubscribeBreaks = onSnapshot(breaksQuery, (breaksSnapshot) => {
            const breaks = breaksSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Trouver si une pause est active
            const activeBreak = breaks.find(b => b.status === "active");
            
            console.log('🔄 Session/Pauses mises à jour en temps réel:', {
              sessionId: sessionDoc.id,
              breaksCount: breaks.length,
              hasActiveBreak: !!activeBreak
            });
            
            setSessionState({
              status:                activeBreak ? "on_break" : "working",
              startTime:             startTime,
              currentBreakStartTime: activeBreak ? activeBreak.startTime.toDate() : null,
              sessionId:             sessionDoc.id,
              activeBreakId:         activeBreak ? activeBreak.id : null,
              breakType:             activeBreak ? (activeBreak.breakType || "regular") : null,
              localPauseTime:        sessionData.totalPauseTime || 0,
              position:              sessionData.startLocation || null,
              breaks:                breaks
            });
          });
          
          // Nettoyer le listener des pauses si la session change
          return () => unsubscribeBreaks();
          
        } else {
          // Aucune session dans Firebase — vérifier la queue locale
          const localState = reconstructStateFromQueue(userEmail);
          if (localState) {
            console.log('📦 Session trouvée dans la queue locale (pas encore syncée)', localState.sessionId);
            setSessionState(localState);
          } else {
            setSessionState(initialState);
          }
        }
        
        setIsLoadingSession(false);
      } catch (err) {
        console.error("Erreur lors de la vérification de session active:", err);
        setIsLoadingSession(false);
      }
    });

    // Nettoyer le listener quand le composant se démonte ou que userEmail change
    return () => {
      console.log('🔕 Désinstallation du listener de session');
      unsubscribeSession();
    };
  }, [userEmail]); // isOnline retiré : Firebase SDK gère la reconnexion automatiquement

  /* ----------------------- Style de carte ----------------------- */
  useEffect(() => {
    const fetchMapStyle = async () => {
      try {
        const configDoc = await getDoc(doc(db, "adminConfigs", "global"));
        if (configDoc.exists()) {
          const data = configDoc.data();
          if (data.mapStyle) {
            const selectedStyle = MAP_STYLES.find(style => style.key === data.mapStyle) || MAP_STYLES[0];
            setMapStyle(selectedStyle);
          }
          if (data.minLunchBreakMinutes) {
            setMinLunchBreakMs(Number(data.minLunchBreakMinutes) * 60 * 1000);
          }
        }
        setIsMapStyleLoaded(true);
      } catch (error) {
        console.error("Erreur lors de la récupération du style de carte:", error);
        setIsMapStyleLoaded(true);
      }
    };

    if (userEmail) {
      fetchMapStyle();
    }
  }, [userEmail]);

  /* ----------------------- GPS Helper ----------------------- */
  const getCurrentPosition = useCallback(async () => {
    try {
      setIsLocationActive(true);
      
      const pos = await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 3;
        
        const tryGetPosition = () => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (position.coords.accuracy <= MIN_ACCURACY) {
                resolve({
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  accuracy: position.coords.accuracy
                });
              } else {
                attempts++;
                if (attempts < maxAttempts) {
                  setTimeout(tryGetPosition, 2000);
                } else {
                  reject(new Error("Précision GPS insuffisante après 3 tentatives"));
                }
              }
            },
            (error) => {
              if (error.code === 1) {
                // Permission refusée
                reject(error);
              } else {
                attempts++;
                if (attempts < maxAttempts) {
                  setTimeout(tryGetPosition, 2000);
                } else {
                  reject(error);
                }
              }
            },
            { 
              enableHighAccuracy: true, 
              timeout: 10000 + (attempts * 5000), 
              maximumAge: 0 
            }
          );
        };
        
        tryGetPosition();
      });
      
      if (isMapStyleLoaded && mapRef.current) {
        setSessionState((p) => ({ ...p, position: pos }));
        mapRef.current.setView([pos.lat, pos.lng], 16);
      }
      
      return pos;
    } catch (e) {
      if (e.code === 1) {
        setGpsPermissionDenied(true);
      }
      throw e;
    } finally {
      setTimeout(() => setIsLocationActive(false), 500);
    }
  }, [isMapStyleLoaded]);

  /* ----------------------- Mise à jour position automatique ----------------------- */
  useEffect(() => {
    if (!isMapStyleLoaded) return;
    
    const updatePosition = async () => {
      try {
        await getCurrentPosition();
      } catch (err) {
        console.error("Erreur mise à jour position:", err);
      }
    };
    
    updatePosition();
    const interval = setInterval(updatePosition, 20 * 60 * 1000); // Toutes les 20 min
    
    return () => clearInterval(interval);
  }, [isMapStyleLoaded, getCurrentPosition]);

  /* ----------------------- Compteur pause déjeuner ----------------------- */
  useEffect(() => {
    if (sessionState.status !== "on_break" || sessionState.breakType !== "lunch") {
      setRemainingLunchSec(null);
      return;
    }

    const tick = () => {
      const elapsed = Date.now() - (sessionState.currentBreakStartTime?.getTime() || Date.now());
      const remaining = Math.ceil((minLunchBreakMs - elapsed) / 1000);
      setRemainingLunchSec(remaining > 0 ? remaining : 0);
    };

    tick(); // calcul immédiat
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sessionState.status, sessionState.breakType, sessionState.currentBreakStartTime, minLunchBreakMs]);

  /* ----------------------- Fonctions Firebase directes ----------------------- */
  
  const finalizeSessionInFirebase = async (sessionId, endPosition) => {
    try {
      const sessionRef = doc(db, "workSessions", sessionId);
      const sessionDoc = await getDoc(sessionRef);
      
      if (!sessionDoc.exists()) {
        throw new Error("Session introuvable");
      }
      
      const sessionData = sessionDoc.data();
      const startTime = sessionData.startTime.toDate();
      const endTime = new Date();
      
      // Récupérer toutes les pauses
      const breaksQuery = query(
        collection(db, "breaks"),
        where("sessionId", "==", sessionId)
      );
      const breaksSnapshot = await getDocs(breaksQuery);
      
      let totalPauseTime = 0;
      const breaksArray = [];
      
      breaksSnapshot.forEach(doc => {
        const breakData = doc.data();
        if (breakData.status === "completed" && breakData.duration) {
          totalPauseTime += breakData.duration;
        }
        breaksArray.push({
          startTime: breakData.startTime,
          endTime: breakData.endTime,
          startLocation: breakData.startLocation,
          endLocation: breakData.endLocation,
          duration: breakData.duration || 0
        });
      });
      
      const totalSessionTime = endTime.getTime() - startTime.getTime();
      const totalWorkTime = totalSessionTime - totalPauseTime;
      
      // ✨ NOUVEAU : Calculer les horaires ajustés selon les règles
      console.log('⏰ Calcul des horaires ajustés...');
      const adjustedTimes = await calculateAdjustedTimes(
        { startTime, endTime },
        userEmail
      );
      
      console.log('📊 Horaires ajustés:', {
        start: {
          real: startTime.toLocaleTimeString('fr-FR'),
          adjusted: adjustedTimes.adjustedStartTime.toLocaleTimeString('fr-FR')
        },
        end: {
          real: endTime.toLocaleTimeString('fr-FR'),
          adjusted: adjustedTimes.adjustedEndTime?.toLocaleTimeString('fr-FR')
        },
        rule: adjustedTimes.appliedRule
      });
      
      // Calculer le temps de travail ajusté
      const adjustedWorkTime = calculateAdjustedWorkTime(
        adjustedTimes.adjustedStartTime,
        adjustedTimes.adjustedEndTime,
        totalPauseTime
      );
      
      // Mettre à jour la session avec horaires réels ET ajustés
      await updateDoc(sessionRef, {
        // Horaires RÉELS
        startTime: Timestamp.fromDate(startTime),
        endTime: Timestamp.fromDate(endTime),
        
        // Horaires AJUSTÉS (calculés)
        adjustedStartTime: Timestamp.fromDate(adjustedTimes.adjustedStartTime),
        adjustedEndTime: adjustedTimes.adjustedEndTime ? Timestamp.fromDate(adjustedTimes.adjustedEndTime) : null,
        
        // Métadonnées
        appliedRule: adjustedTimes.appliedRule,
        
        endLocation: endPosition,
        status: "completed",
        totalSessionTime: totalSessionTime,
        totalPauseTime: totalPauseTime,
        totalWorkTime: totalWorkTime,
        adjustedWorkTime: adjustedWorkTime, // Temps de travail basé sur horaires ajustés
        breaks: breaksArray
      });
      
      return {
        totalSessionTime,
        totalPauseTime,
        totalWorkTime,
        adjustedWorkTime
      };
    } catch (err) {
      console.error("Erreur finalisation session:", err);
      throw err;
    }
  };

  /* ----------------------- Actions de pointage ----------------------- */
  
  const startWorkday = async () => {
    // Vérifier qu'il n'y a pas déjà une session locale en cours
    if (sessionState.status !== "idle") {
      setError("⚠️ Une session est déjà en cours.");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const startTime = new Date();
      const sessionId = uuidv4();
      const pos = await getCurrentPosition();
      mapRef.current?.setView([pos.lat, pos.lng], 16);

      const action = {
        type: "START_WORK",
        sessionId,
        employeeId:    auth.currentUser.uid,
        employeeEmail: userEmail,
        employeeName:  `${userData?.prenom} ${userData?.nom}`,
        siteDeTravail: userData?.siteDeTravail || "Non défini",
        timestamp:     startTime.getTime(),
        location:      { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy },
        device: {
          userAgent:        navigator.userAgent,
          platform:         navigator.platform,
          language:         navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
        },
      };

      // Mise à jour de l'écran immédiatement
      setSessionState({
        status:               "working",
        startTime:            startTime,
        currentBreakStartTime: null,
        sessionId:            sessionId,
        activeBreakId:        null,
        localPauseTime:       0,
        position:             pos,
        breaks:               [],
      });

      if (navigator.onLine) {
        try {
          await setDoc(doc(db, "workSessions", sessionId), {
            employeeId:    action.employeeId,
            employeeEmail: action.employeeEmail,
            employeeName:  action.employeeName,
            siteDeTravail: action.siteDeTravail,
            startTime:     Timestamp.fromMillis(action.timestamp),
            startLocation: action.location,
            status:        "active",
            isOnBreak:     false,
            totalPauseTime: 0,
            totalWorkTime:  0,
            device:        action.device,
            createdAt:     Timestamp.fromMillis(action.timestamp),
          });
          console.log("✅ Session démarrée dans Firebase:", sessionId);
        } catch (firebaseErr) {
          console.warn("Firebase indisponible, mise en queue:", firebaseErr.message);
          enqueue(action);
          setPendingCount(getPendingCount());
        }
      } else {
        enqueue(action);
        setPendingCount(getPendingCount());
        console.log("📦 Session mise en queue (hors ligne):", sessionId);
      }

    } catch (err) {
      console.error("Erreur démarrage session:", err);
      setError(err.message || "Erreur lors du démarrage");
      setSessionState(initialState);
    } finally {
      setIsProcessing(false);
    }
  };

  const startBreak = async (type = "regular") => {
    if (sessionState.status !== "working") {
      setError("Pas de session active");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const breakStartTime = new Date();
      const breakId = uuidv4(); // ID généré localement pour suivi sans Firebase
      const pos = await getCurrentPosition();
      mapRef.current?.setView([pos.lat, pos.lng], 16);

      const action = {
        type:      "START_BREAK",
        sessionId: sessionState.sessionId,
        breakId,
        breakType: type,
        timestamp: breakStartTime.getTime(),
        location:  { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy },
      };

      // Mise à jour écran immédiatement
      setSessionState((p) => ({
        ...p,
        status:               "on_break",
        currentBreakStartTime: breakStartTime,
        activeBreakId:        breakId,
        breakType:            type,
        position:             pos,
      }));

      if (navigator.onLine) {
        try {
          await setDoc(doc(db, "breaks", breakId), {
            sessionId:     action.sessionId,
            startTime:     Timestamp.fromMillis(action.timestamp),
            startLocation: action.location,
            breakType:     action.breakType,
            status:        "active",
            createdAt:     Timestamp.fromMillis(action.timestamp),
          });
          await updateDoc(doc(db, "workSessions", action.sessionId), {
            isOnBreak:   true,
            activeBreak: { id: breakId, startTime: Timestamp.fromMillis(action.timestamp) },
          });
          console.log("✅ Pause démarrée dans Firebase:", breakId);
        } catch (firebaseErr) {
          console.warn("Firebase indisponible, mise en queue:", firebaseErr.message);
          enqueue(action);
          setPendingCount(getPendingCount());
        }
      } else {
        enqueue(action);
        setPendingCount(getPendingCount());
        console.log("📦 Pause mise en queue (hors ligne):", breakId);
      }

    } catch (err) {
      console.error("Erreur démarrage pause:", err);
      setError(err.message || "Erreur lors du démarrage de la pause");
    } finally {
      setIsProcessing(false);
    }
  };

  const startLunchBreak = () => startBreak("lunch");

  const endBreak = async () => {
    if (sessionState.status !== "on_break") {
      setError("Aucune pause active");
      return;
    }

    // Bloquer si pause déjeuner pas encore terminée
    if (sessionState.breakType === "lunch" && remainingLunchSec > 0) {
      const min = Math.floor(remainingLunchSec / 60);
      const sec = remainingLunchSec % 60;
      setError(`⏱ Pause déjeuner obligatoire — encore ${min > 0 ? `${min} min ` : ""}${sec > 0 ? `${sec} sec` : ""}`);
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const breakEndTime = new Date();
      const pos = await getCurrentPosition();
      mapRef.current?.setView([pos.lat, pos.lng], 16);

      // Calcul local de la durée (ne nécessite pas Firebase)
      const breakStartMs = sessionState.currentBreakStartTime?.getTime() || breakEndTime.getTime();
      const duration = breakEndTime.getTime() - breakStartMs;
      const breakId = sessionState.activeBreakId;

      const action = {
        type:      "END_BREAK",
        sessionId: sessionState.sessionId,
        breakId,
        timestamp: breakEndTime.getTime(),
        location:  { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy },
      };

      // Mise à jour écran immédiatement + cumul pause local
      setSessionState((p) => ({
        ...p,
        status:               "working",
        currentBreakStartTime: null,
        activeBreakId:        null,
        breakType:            null,
        localPauseTime:       (p.localPauseTime || 0) + duration,
        position:             pos,
      }));

      if (navigator.onLine) {
        try {
          // Résoudre l'ID de la pause : depuis l'état local, ou par requête Firebase en fallback
          let resolvedBreakId = breakId;
          if (!resolvedBreakId) {
            const q = query(
              collection(db, "breaks"),
              where("sessionId", "==", action.sessionId),
              where("status", "==", "active")
            );
            const snap = await getDocs(q);
            if (!snap.empty) resolvedBreakId = snap.docs[0].id;
          }

          if (resolvedBreakId) {
            await updateDoc(doc(db, "breaks", resolvedBreakId), {
              endTime:     Timestamp.fromMillis(breakEndTime.getTime()),
              endLocation: action.location,
              status:      "completed",
              duration,
            });
          }
          const sessionDoc = await getDoc(doc(db, "workSessions", action.sessionId));
          if (sessionDoc.exists()) {
            const currentPause = sessionDoc.data().totalPauseTime || 0;
            await updateDoc(doc(db, "workSessions", action.sessionId), {
              isOnBreak:      false,
              activeBreak:    null,
              totalPauseTime: currentPause + duration,
            });
          }
          console.log("✅ Pause terminée dans Firebase:", resolvedBreakId);
        } catch (firebaseErr) {
          console.warn("Firebase indisponible, mise en queue:", firebaseErr.message);
          enqueue(action);
          setPendingCount(getPendingCount());
        }
      } else {
        enqueue(action);
        setPendingCount(getPendingCount());
        console.log("📦 Fin de pause mise en queue (hors ligne):", breakId);
      }

    } catch (err) {
      console.error("Erreur fin de pause:", err);
      setError(err.message || "Erreur lors de la fin de pause");
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmEndWorkday = () => {
    setSessionState((p) => ({ ...p, status: "confirming_end" }));
  };

  const cancelEndWorkday = () => {
    setSessionState((p) => ({ ...p, status: "working" }));
  };

  const endWorkday = async () => {
    if (!sessionState.sessionId) {
      setError("Pas de session active");
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const endTime = new Date();
      const pos = await getCurrentPosition().catch(() => null);
      if (pos) mapRef.current?.setView([pos.lat, pos.lng], 16);

      // Calcul local immédiat pour l'affichage du récap
      const totalPause   = sessionState.localPauseTime || 0;
      const totalSession = endTime.getTime() - (sessionState.startTime?.getTime() || endTime.getTime());
      const totalWork    = Math.max(0, totalSession - totalPause);

      setRecapData({ sessionTime: totalSession, totalPauseTime: totalPause, workTime: totalWork });
      setSessionState((p) => ({ ...p, status: "recap", endTime }));

      if (navigator.onLine) {
        try {
          // Flush les actions en attente avant de finaliser
          if (getPendingCount() > 0) {
            const flushResult = await flushQueue();
            setPendingCount(flushResult.remaining);
          }

          // Finalisation complète dans Firebase (avec horaires ajustés)
          const result = await finalizeSessionInFirebase(
            sessionState.sessionId,
            pos ? { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy } : null
          );

          // Mettre à jour le récap avec les valeurs Firebase (plus précises)
          setRecapData({
            sessionTime:   result.totalSessionTime,
            totalPauseTime: result.totalPauseTime,
            workTime:      result.totalWorkTime,
          });
          console.log("✅ Session finalisée dans Firebase");

        } catch (firebaseErr) {
          console.warn("Firebase indisponible, mise en queue:", firebaseErr.message);
          enqueue({
            type:      "END_WORK",
            sessionId: sessionState.sessionId,
            timestamp: endTime.getTime(),
            location:  pos ? { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy } : null,
          });
          setPendingCount(getPendingCount());
        }
      } else {
        enqueue({
          type:      "END_WORK",
          sessionId: sessionState.sessionId,
          timestamp: endTime.getTime(),
          location:  pos ? { latitude: pos.lat, longitude: pos.lng, accuracy: pos.accuracy } : null,
        });
        setPendingCount(getPendingCount());
        console.log("📦 Fin de journée mise en queue (hors ligne)");
      }

    } catch (err) {
      console.error("Erreur fin de session:", err);
      setError(err.message || "Erreur lors de la fin de session");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ----------------------- Autres actions ----------------------- */
  
  const handleLogout = async () => {
    await auth.signOut();
    navigate("/");
  };

  const handleReturnToStart = async () => {
    setSessionState(initialState);
    
    if (isMapStyleLoaded) {
      try {
        await getCurrentPosition();
      } catch (error) {
        console.log("Erreur lors de la récupération de la position au retour:", error);
      }
    }
  };

  const handleDisclaimerAccept = async () => {
    try {
      if (!employeeDocId) {
        setError("Erreur : Impossible de mettre à jour le profil. Veuillez rafraîchir la page.");
        return;
      }
      
      const employeeRef = doc(db, "employees", employeeDocId);
      await updateDoc(employeeRef, {
        hasAcceptedDisclaimer: true,
        disclaimerAcceptedAt: new Date()
      });
      
      setShowDisclaimer(false);
      setUserData(prev => ({
        ...prev,
        hasAcceptedDisclaimer: true,
        disclaimerAcceptedAt: new Date()
      }));
      
    } catch (error) {
      console.error("Erreur lors de la mise à jour du disclaimer:", error);
      setError("Erreur lors de l'enregistrement du consentement: " + error.message);
    }
  };

  /* ----------------------- Notifications EN TEMPS RÉEL ----------------------- */
  useEffect(() => {
    if (!userEmail) return;

    console.log("🔔 Installation des listeners de notifications pour:", userEmail);

    // Listener temps réel pour les absences
    const absencesQuery = query(
      collection(db, "absences"),
      where("employeeEmail", "==", userEmail),
      where("vuParEmploye", "==", false),
      where("status", "in", ["Validé", "Refusé"])
    );

    const unsubscribeAbsences = onSnapshot(absencesQuery, (snapshot) => {
      const absenceNotifs = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        type: 'absence',
        ...doc.data() 
      }));
      console.log("🔔 Notifications d'absences mises à jour:", absenceNotifs.length);
      console.log("📋 Détails des notifications:", absenceNotifs);
      setNotifications(absenceNotifs);
    }, (error) => {
      console.error("❌ ERREUR listener absences:", error);
      console.error("❌ Code erreur:", error.code);
      console.error("❌ Message:", error.message);
    });

    // Nettoyage des listeners quand le composant se démonte
    return () => {
      unsubscribeAbsences();
    };
  }, [userEmail]);

  /* ----------------------- Rendu écrans ----------------------- */
  const renderScreen1 = () => (
    <div className="screen-1">
      <div className="welcome-message">
        <h2>Bienvenue {userData?.prenom || "Employé"}</h2>
        <p>Prêt à commencer votre journée ?</p>
      </div>
    </div>
  );

  const renderScreen2 = () => (
    <div className="screen-2">
      <div className="work-time">
        <h3 style={{ color: '#4CAF50', fontSize: '24px', marginBottom: '16px' }}>
          🟢 En session
        </h3>
        <p style={{ fontSize: '16px', color: '#666' }}>
          Pointage en cours depuis {sessionState.startTime?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );

  const renderScreen3 = () => {
    const isLunch = sessionState.breakType === "lunch";
    const lunchMin = Math.floor(minLunchBreakMs / 60000);
    const remainMin = remainingLunchSec !== null ? Math.floor(remainingLunchSec / 60) : 0;
    const remainSec = remainingLunchSec !== null ? remainingLunchSec % 60 : 0;
    const lunchDone = remainingLunchSec !== null && remainingLunchSec <= 0;

    return (
      <div className="screen-3">
        <div className="break-time">
          <h3 style={{ color: '#FF9800', fontSize: '24px', marginBottom: '16px' }}>
            {isLunch ? "🍽️ Pause déjeuner" : "☕ En pause"}
          </h3>
          <p style={{ fontSize: '16px', color: '#666' }}>
            Pause démarrée à {sessionState.currentBreakStartTime?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </p>

          {isLunch && remainingLunchSec !== null && (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              {!lunchDone ? (
                <>
                  <div style={{
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#FF9800',
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '2px'
                  }}>
                    {String(remainMin).padStart(2, '0')}:{String(remainSec).padStart(2, '0')}
                  </div>
                  <p style={{ fontSize: '14px', color: '#888', marginTop: '8px' }}>
                    Pause minimale : {lunchMin} min
                  </p>
                </>
              ) : (
                <p style={{ fontSize: '16px', color: '#4CAF50', fontWeight: '600' }}>
                  ✅ Pause minimale atteinte — vous pouvez reprendre
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderScreen4 = () => (
    <div className="screen-4">
      <div className="summary">
        <h3>Voulez-vous vraiment terminer votre journée ?</h3>
      </div>
    </div>
  );

  const renderScreenRecap = () => (
    <div className="screen-recap">
      <div className="summary summary-large" style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 20, marginBottom: 10, textAlign: "center" }}>
            Bonne fin de journée {userData?.prenom || ""} !
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 18, letterSpacing: 1, marginBottom: 6, textAlign: "center" }}>
              Temps de Travail Effectif
            </div>
            <div style={{ fontWeight: "bold", fontSize: 32, color: "#2ecc71", textAlign: "center" }}>
              {formatTime(recapData.workTime)}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 18, letterSpacing: 1, marginBottom: 6, textAlign: "center" }}>
              Temps de Pause(s)
            </div>
            <div style={{ fontWeight: "bold", fontSize: 32, color: "#FF9800", textAlign: "center" }}>
              {formatTime(recapData.totalPauseTime)}
            </div>
          </div>
          <div style={{ fontWeight: 500, fontSize: 18, marginTop: 18, textAlign: "center" }}>
            À demain.
          </div>
        </div>
      </div>
    </div>
  );

  const renderScreen5 = () => (
    <div className="screen-5">
      <div className="summary">
        <h3>Journée terminée</h3>
        <p>Temps de Session : {formatTime(recapData.sessionTime)}</p>
        <p>Temps de pause : {formatTime(recapData.totalPauseTime)}</p>
        <p>Temps de travail effectif : {formatTime(recapData.workTime)}</p>
      </div>
      <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
        <button className="start" onClick={handleReturnToStart}>
          Retour
        </button>
        <button
          className="history"
          onClick={() => navigate("/dashboard-board")}
          style={{position: 'relative'}}
        >
          Tableau de bord
          {(notifications.length > 0) && (
            <span style={{
              position: 'absolute', 
              top: 8, 
              right: 12, 
              minWidth: 16, 
              height: 16, 
              background: '#ffeb3b', 
              borderRadius: '50%', 
              border: '2px solid #fff', 
              boxShadow: '0 0 0 2px #ffeb3b', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {notifications.length}
            </span>
          )}
        </button>
        <button className="end" onClick={handleLogout}>
          Déconnexion
        </button>
      </div>
    </div>
  );

  const renderScreen = () => {
    switch (sessionState.status) {
      case "idle":
        return renderScreen1();
      case "working":
        return renderScreen2();
      case "on_break":
        return renderScreen3();
      case "confirming_end":
        return renderScreen4();
      case "recap":
        return renderScreenRecap();
      case "ended":
        return renderScreen5();
      default:
        return null;
    }
  };

  /* ----------------------- Indicateurs ----------------------- */
  const renderStatusIndicators = () => (
    <div className="status-indicators">
      <div className={`connection-status ${isOnline ? "online" : "offline"}`}>
        {isOnline ? "En ligne" : "Hors ligne"}
      </div>
      {pendingCount > 0 && (
        <div className="sync-pending" title={`${pendingCount} action(s) en attente de synchronisation`}>
          ⏳ Sync en attente ({pendingCount})
        </div>
      )}
    </div>
  );

  /* ----------------------- Boutons ----------------------- */
  const renderButtons = () => (
    <div className="button-group">
      {sessionState.status === "idle" && (
        <>
          <button
            className="start"
            onClick={startWorkday}
            disabled={isProcessing || isLoadingSession}
            title={isLoadingSession ? "Vérification en cours..." : ""}
          >
            {isLoadingSession ? "⏳ Chargement..." : "Démarrer"}
          </button>
          <button
            className="history"
            onClick={() => navigate("/dashboard-board")}
            style={{position: 'relative'}}
          >
            Tableau de bord
            {(notifications.length > 0) && (
              <span style={{
                position: 'absolute', 
                top: 8, 
                right: 12, 
                minWidth: 16, 
                height: 16, 
                background: '#ffeb3b', 
                borderRadius: '50%', 
                border: '2px solid #fff', 
                boxShadow: '0 0 0 2px #ffeb3b', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                {notifications.length}
              </span>
            )}
          </button>
          <button className="end" onClick={handleLogout}>
            Déconnexion
          </button>
        </>
      )}
      {sessionState.status === "working" && (
        <>
          <button
            className="pause"
            onClick={() => startBreak("regular")}
            disabled={isProcessing}
          >
            Pause
          </button>
          <button
            className="pause"
            onClick={startLunchBreak}
            disabled={isProcessing}
            style={{ background: '#FF9800' }}
          >
            🍽️ Pause déjeuner
          </button>
          <button
            className="end"
            onClick={confirmEndWorkday}
            disabled={isProcessing}
          >
            Terminer
          </button>
        </>
      )}
      {sessionState.status === "on_break" && (
        <button
          className="end"
          onClick={endBreak}
          disabled={isProcessing || (sessionState.breakType === "lunch" && remainingLunchSec > 0)}
          title={
            sessionState.breakType === "lunch" && remainingLunchSec > 0
              ? `Encore ${Math.floor(remainingLunchSec / 60)}min ${remainingLunchSec % 60}sec de pause obligatoire`
              : ""
          }
          style={
            sessionState.breakType === "lunch" && remainingLunchSec > 0
              ? { opacity: 0.5, cursor: 'not-allowed' }
              : {}
          }
        >
          {sessionState.breakType === "lunch" ? "Fin de pause déjeuner" : "Fin de pause"}
        </button>
      )}
      {sessionState.status === "confirming_end" && (
        <>
          <button
            className="end"
            onClick={endWorkday}
            disabled={isProcessing}
          >
            Confirmer
          </button>
          <button className="cancel" onClick={cancelEndWorkday} disabled={isProcessing}>
            Annuler
          </button>
        </>
      )}
      {sessionState.status === "recap" && (
        <>
          <button className="start" onClick={handleReturnToStart}>Retour</button>
          <button
            className="history"
            onClick={() => navigate("/dashboard-board")}
            style={{position: 'relative'}}
          >
            Tableau de bord
            {(notifications.length > 0) && (
              <span style={{
                position: 'absolute', 
                top: 8, 
                right: 12, 
                minWidth: 16, 
                height: 16, 
                background: '#ffeb3b', 
                borderRadius: '50%', 
                border: '2px solid #fff', 
                boxShadow: '0 0 0 2px #ffeb3b', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                {notifications.length}
              </span>
            )}
          </button>
          <button className="end" onClick={handleLogout}>Déconnexion</button>
        </>
      )}
    </div>
  );

  /* ----------------------- Rendu global ----------------------- */
  return (
    <div className="employee-dashboard">
      {gpsPermissionDenied && (
        <div className="gps-overlay">
          <div className="gps-message">
            <h2>Géolocalisation requise 🚫</h2>
            <p>Activez la géolocalisation pour pouvoir pointer.</p>
            <button onClick={() => setGpsPermissionDenied(false)}>Réessayer</button>
          </div>
        </div>
      )}

      {renderStatusIndicators()}

      <div className="map-container">
        <MapContainer
          center={
            sessionState.position
              ? [sessionState.position.lat, sessionState.position.lng]
              : [48.8566, 2.3522]
          }
          zoom={16}
          style={{ height: "100vh", width: "100%" }}
          ref={mapRef}
          zoomControl={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          dragging={false}
          touchZoom={false}
        >
          <TileLayer
            url={mapStyle.url}
            attribution={mapStyle.attribution}
          />
          {sessionState.position && sessionState.position.lat && sessionState.position.lng && (
            <Marker
              position={[sessionState.position.lat, sessionState.position.lng]}
              icon={createLocationIcon(isLocationActive)}
            />
          )}
        </MapContainer>
      </div>

      <div
        className="content-overlay"
        style={{
          pointerEvents:
            sessionState.status === "recap" || sessionState.status === "ended"
              ? "auto"
              : "none",
        }}
      >
        {renderScreen()}
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button 
            onClick={() => setError(null)}
            style={{
              marginLeft: '10px',
              padding: '5px 10px',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {renderButtons()}

      <div className="dashboard-board-content" style={{position: 'relative'}}>
        <AbsenceNotificationBlock
          absenceNotifications={notifications}
          onConsulterAbsence={async (id) => {
            setShowAbsencePopup(id);
            await updateDoc(doc(db, "absences", id), { vuParEmploye: true });
            const user = auth.currentUser;
            if (user) {
              const q = query(
                collection(db, "absences"),
                where("employeeEmail", "==", user.email),
                orderBy("createdAt", "desc")
              );
              const snap = await getDocs(q);
              const notifs = snap.docs.filter(docu => {
                const d = docu.data();
                return (d.status === 'Validé' || d.status === 'Refusé') && d.vuParEmploye === false;
              }).map(docu => ({ id: docu.id, type: 'absence', ...docu.data() }));
              setNotifications(notifs);
            }
          }}
        />
        {showAbsencePopup && (
          <AbsencePopup id={showAbsencePopup} onClose={() => setShowAbsencePopup(null)} />
        )}
      </div>

      {showDisclaimer && (
        <DisclaimerModal onAccept={handleDisclaimerAccept} />
      )}
    </div>
  );
}

/* ──────────────────────────────── Notifications absences uniquement ──────────────────────────────── */
function AbsenceNotificationBlock({ absenceNotifications, onConsulterAbsence }) {
  if (!absenceNotifications || absenceNotifications.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        background: '#fffde7', borderRadius: 10, padding: 16, marginBottom: 12,
        boxShadow: '0 2px 8px #ffe08255', border: '1px solid #ffe082',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 20 }}>✉️</span>
          <span style={{ fontWeight: 600, fontSize: 17 }}>Notification d'absence</span>
        </div>
        {absenceNotifications.map((notif) => (
          <div key={notif.id} style={{ fontSize: 13, color: '#444', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            Notification de retour de demande d'absence
            <button style={{
              marginLeft: 8, fontSize: 13, background: '#ffeb3b', color: '#333',
              border: 'none', borderRadius: 8, padding: '2px 12px', fontWeight: 600, cursor: 'pointer'
            }} onClick={async () => await onConsulterAbsence(notif.id)}>Consulter</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EmployeeDashboard;